// src/components/PositionsGrid.jsx - Updated with unallocated balance display
import React, { useState, useEffect } from 'react';
import { formatAddress, formatPercentage, formatBalance } from '../utils/formatters';

// This component displays the fund positions and includes unallocated ETH in the calculations

const PositionsGrid = ({ 
  positions = [], 
  provider, 
  contract, 
  account, 
  ethPrice, 
  unallocatedEthBalance = "0",
  fundTokenBalance = "0", 
  walletTokenBalance = "0", 
  totalSupply = "0"
}) => {
  const [showTransactions, setShowTransactions] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const transactionsPerPage = 10;

  // Add debugging log
  console.log("PositionsGrid render - props received:", { 
    positionsCount: positions.length, 
    hasProvider: !!provider, 
    hasContract: !!contract, 
    hasAccount: !!account, 
    ethPrice,
    unallocatedEthBalance
  });

  // Parse unallocated ETH
  const unallocatedEth = parseFloat(unallocatedEthBalance) || 0;
  const unallocatedEthValueUsd = unallocatedEth * ethPrice;

  // Calculate total USD value including unallocated ETH
  const positionsValueUsd = positions.reduce((sum, position) => sum + (position.valueUsd || 0), 0);
  const totalValueUsd = positionsValueUsd + unallocatedEthValueUsd;
  
  // Calculate user ownership percentage
  let userOwnershipPercentage = 0;
  if (parseFloat(totalSupply) > 0) {
    const userTokens = parseFloat(fundTokenBalance) + parseFloat(walletTokenBalance);
    userOwnershipPercentage = (userTokens / parseFloat(totalSupply)) * 100;
  }
  
  // Calculate user's portion of the fund value
  const userValueUsd = totalValueUsd * (userOwnershipPercentage / 100);

  const toggleTransactions = () => {
    console.log("Toggle function called, current state:", showTransactions);
    setShowTransactions(!showTransactions);
  };

  // Listen for external toggle events
  useEffect(() => {
    const handleExternalToggle = () => {
      console.log("External toggle event received");
      toggleTransactions();
    };
    
    // Add event listener
    document.addEventListener('toggleTransactions', handleExternalToggle);
    
    // Clean up
    return () => {
      document.removeEventListener('toggleTransactions', handleExternalToggle);
    };
  }, [showTransactions]); // Include showTransactions in dependencies


// Format transaction value in ETH with proper unit conversion
const formatTransactionValue = (value) => {
  if (!value) return '0.0000 ETH';
  
  try {
    // Remove 'ETH' suffix if present
    const valueStr = value.toString().replace(' ETH', '');
    
    // Parse the value
    const valueNum = parseFloat(valueStr);
    
    // Check if it's a very large number (likely in wei)
    if (valueNum > 1000000000000000) {
      // Convert from wei to ETH (1 ETH = 10^18 wei)
      const ethValue = valueNum / 1000000000000000000;
      return `${ethValue.toFixed(4)} ETH`;
    }
    
    // Handle normal values
    return `${valueNum.toFixed(4)} ETH`;
  } catch (error) {
    console.error("Error formatting transaction value:", error);
    return value; // Return the original value if there's an error
  }
};


  // Function to fetch transactions
  const fetchTransactions = async () => {
    if (!provider || !contract || !account) {
      console.error("Missing required props for fetching transactions:", { 
        hasProvider: !!provider, 
        hasContract: !!contract, 
        hasAccount: !!account 
      });
      return;
    }
    
    console.log("Starting to fetch transactions");
    setIsLoading(true);
    setError(null);
    
    try {
      // Get contract creation block for efficiency
      const contractDeployedFilter = {
        address: contract.address,
        fromBlock: 0, // You might want to set a more recent starting block in production
      };
      
      // Define events to track
      const depositFilter = contract.filters.Deposit(null);
      const sharesMintedFilter = contract.filters.SharesMinted(null);
      const redemptionFilter = contract.filters.IndexTokensRedeemed(null);
      const tokenRedeemedFilter = contract.filters.TokenRedeemed(null);
      const addedToFundFilter = contract.filters.TokensAddedToFund(null);
      const withdrawnFromFundFilter = contract.filters.TokensWithdrawnFromFund(null);
      
      // Get event logs
      console.log("Fetching deposit logs");
      const depositLogs = await provider.getLogs({
        ...depositFilter,
        ...contractDeployedFilter
      });
      
      console.log("Fetching shares minted logs");
      const sharesMintedLogs = await provider.getLogs({
        ...sharesMintedFilter,
        ...contractDeployedFilter
      });
      
      console.log("Fetching redemption logs");
      const redemptionLogs = await provider.getLogs({
        ...redemptionFilter,
        ...contractDeployedFilter
      });
      
      console.log("Fetching token redeemed logs");
      const tokenRedeemedLogs = await provider.getLogs({
        ...tokenRedeemedFilter,
        ...contractDeployedFilter
      });
      
      console.log("Fetching added to fund logs");
      const addedToFundLogs = await provider.getLogs({
        ...addedToFundFilter,
        ...contractDeployedFilter
      });
      
      console.log("Fetching withdrawn from fund logs");
      const withdrawnFromFundLogs = await provider.getLogs({
        ...withdrawnFromFundFilter,
        ...contractDeployedFilter
      });
      
      // Combine all logs
      const allLogs = [
        ...depositLogs.map(log => ({ ...log, eventName: 'Deposit' })),
        ...sharesMintedLogs.map(log => ({ ...log, eventName: 'Shares Minted' })),
        ...redemptionLogs.map(log => ({ ...log, eventName: 'Tokens Redeemed' })),
        ...tokenRedeemedLogs.map(log => ({ ...log, eventName: 'Token Redeemed' })),
        ...addedToFundLogs.map(log => ({ ...log, eventName: 'Added to Fund' })),
        ...withdrawnFromFundLogs.map(log => ({ ...log, eventName: 'Withdrawn from Fund' }))
      ];
      
      console.log(`Found ${allLogs.length} total logs`);
      
      // Sort by block number (descending)
      allLogs.sort((a, b) => b.blockNumber - a.blockNumber);
      
      // Get transaction details for each log
      const txData = await Promise.all(
        allLogs.map(async (log, index) => {
          try {
            console.log(`Processing log ${index + 1}/${allLogs.length}`);
            const tx = await provider.getTransaction(log.transactionHash);
            const receipt = await provider.getTransactionReceipt(log.transactionHash);
            const block = await provider.getBlock(log.blockNumber);
            
            // Parse event data based on event type
            let eventData = {};
            
            if (log.eventName === 'Deposit') {
              const parsedLog = contract.interface.parseLog(log);
              eventData = {
                user: parsedLog.args.user,
                amount: formatBalance(parsedLog.args.amount, 4) + ' ETH'
              };
            } else if (log.eventName === 'Shares Minted') {
              const parsedLog = contract.interface.parseLog(log);
              eventData = {
                user: parsedLog.args.user,
                amount: formatBalance(parsedLog.args.amount, 4) + ' ETH',
                tokens: formatBalance(parsedLog.args.tokensMinted, 4) + ' MAINXX'
              };
            } else if (log.eventName === 'Tokens Redeemed') {
              const parsedLog = contract.interface.parseLog(log);
              eventData = {
                user: parsedLog.args.user,
                tokens: formatBalance(parsedLog.args.tokensRedeemed, 4) + ' MAINXX',
                ethReceived: formatBalance(parsedLog.args.ethReceived, 4) + ' ETH'
              };
            } else if (log.eventName === 'Token Redeemed') {
              const parsedLog = contract.interface.parseLog(log);
              eventData = {
                user: parsedLog.args.user,
                tokenAddress: parsedLog.args.tokenAddress,
                amount: formatBalance(parsedLog.args.amount, 4),
                fee: formatBalance(parsedLog.args.fee, 4)
              };
            } else {
              const parsedLog = contract.interface.parseLog(log);
              eventData = {
                user: parsedLog.args.user,
                amount: formatBalance(parsedLog.args.amount, 4)
              };
            }
            
            return {
              txHash: tx.hash,
              blockNumber: tx.blockNumber,
              timestamp: block.timestamp,
              from: tx.from,
              to: tx.to,
              value: formatBalance(tx.value, 4) + ' ETH',
              gasUsed: receipt.gasUsed.toString(),
              gasPrice: formatBalance(tx.gasPrice, 9) + ' Gwei',
              eventName: log.eventName,
              eventData
            };
          } catch (error) {
            console.error("Error processing transaction:", error, log);
            return null;
          }
        })
      );
      
      // Filter out nulls (failed tx processing)
      const validTxData = txData.filter(tx => tx !== null);
      console.log(`Successfully processed ${validTxData.length} transactions`);
      
      setTransactions(validTxData);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setError("Failed to load transaction history. Please try again.");
    } finally {
      setIsLoading(false);
      console.log("Finished fetching transactions");
    }
  };

  // Fetch transactions when component mounts or dependencies change
  useEffect(() => {
    console.log("useEffect triggered, showTransactions:", showTransactions);
    if (showTransactions) {
      fetchTransactions();
    }
  }, [showTransactions, provider, contract, account]);

  // Format timestamp to human-readable date
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  // Calculate time ago
  const getTimeAgo = (timestamp) => {
    const seconds = Math.floor((Date.now() / 1000) - timestamp);
    
    if (seconds < 60) return `${seconds} sec${seconds !== 1 ? 's' : ''} ago`;
    
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min${minutes !== 1 ? 's' : ''} ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr${hours !== 1 ? 's' : ''} ago`;
    
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
    
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
    
    const years = Math.floor(months / 12);
    return `${years} year${years !== 1 ? 's' : ''} ago`;
  };

  // Get block explorer URL based on network
const getExplorerUrl = (txHash) => {
  // Check if we're on a test network
  if (provider) {
    try {
      // Try to get the network from the provider
      const network = provider.network?.name;
      
      // If we have network info, use the appropriate URL
      if (network) {
        if (network === 'homestead' || network === 'mainnet') {
          return `https://etherscan.io/tx/${txHash}`;
        } else if (network === 'sepolia') {
          return `https://sepolia.etherscan.io/tx/${txHash}`;
        } else if (network === 'goerli') {
          return `https://goerli.etherscan.io/tx/${txHash}`;
        } else if (network === 'matic' || network === 'polygon') {
          return `https://polygonscan.com/tx/${txHash}`;
        } else if (network.includes('optimism')) {
          return `https://optimistic.etherscan.io/tx/${txHash}`;
        } else if (network.includes('arbitrum')) {
          return `https://arbiscan.io/tx/${txHash}`;
        } else {
          // For other networks, try to construct a URL
          return `https://${network}.etherscan.io/tx/${txHash}`;
        }
      }
    } catch (error) {
      console.error("Error determining network:", error);
    }
  }
  
  // Default to mainnet Etherscan if we can't determine the network
  return `https://etherscan.io/tx/${txHash}`;
};


// Calculate gas fee in ETH with proper unit conversion
const calculateGasFee = (gasUsed, gasPriceGwei) => {
  if (!gasUsed || !gasPriceGwei) return 'N/A';
  
  try {
    // Parse the gas price (remove ' Gwei' suffix if present)
    const gweiString = gasPriceGwei.replace(' Gwei', '');
    
    // Convert wei to gwei (1 gwei = 10^9 wei)
    // Then convert gwei to ETH (1 ETH = 10^9 gwei)
    const gweiPerEth = 1000000000000000000;
    
    // Check if gasUsed is a BigNumber object (ethers.js) or a string
    const gasUsedValue = typeof gasUsed === 'object' && gasUsed.toString ? 
      gasUsed.toString() : gasUsed;
    
    // Make sure we're working with numbers
    const gasUsedNum = parseFloat(gasUsedValue);
    const gweiNum = parseFloat(gweiString);
    
    // Calculate gas fee: gasUsed * gasPrice (in gwei) / 10^9 = ETH
    const gasFeeETH = (gasUsedNum * gweiNum) / gweiPerEth;
    
    // Format the result with maximum 6 decimal places
    // Use exponential notation for very small values
    if (gasFeeETH < 0.000001) {
      return `${gasFeeETH.toExponential(2)} ETH`;
    }
    
    return `${formatBalance(gasFeeETH, 6)} ETH`;
  } catch (error) {
    console.error("Error calculating gas fee:", error);
    return 'N/A';
  }
};

  // Calculate pagination
  const indexOfLastTx = currentPage * transactionsPerPage;
  const indexOfFirstTx = indexOfLastTx - transactionsPerPage;
  const currentTransactions = transactions.slice(indexOfFirstTx, indexOfLastTx);
  const totalPages = Math.ceil(transactions.length / transactionsPerPage);

  // If no positions and not showing transactions
  if ((!positions || positions.length === 0) && unallocatedEth <= 0 && !showTransactions) {
    return (
      <p className="text-gray-500 italic">No positions found in fund</p>
    );
  }

  return (
    <>
      {/* Toggle button has been removed from here */}
      
      {!showTransactions ? (
        // Fund Positions View
        <div>
          {/* Fund value summary information */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-600">Total Fund Value</h4>
                <p className="text-lg font-bold text-blue-800">${totalValueUsd.toFixed(2)}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-600">Your Balance</h4>
                <p className="text-lg font-bold text-blue-800">${userValueUsd.toFixed(2)}</p>
              </div>
            </div>
          </div>
        
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-md font-bold text-gray-900 tracking-wider">
                  Token
                </th>
                <th scope="col" className="px-4 py-3 text-left text-md font-bold text-gray-900 tracking-wider">
                  Target %
                </th>
                <th scope="col" className="px-4 py-3 text-left text-md font-bold text-gray-900 tracking-wider">
                  Balance
                </th>
                <th scope="col" className="px-4 py-3 text-left text-md font-bold text-gray-900 tracking-wider">
                  Price (USD)
                </th>
                <th scope="col" className="px-4 py-3 text-left text-md font-bold text-gray-900 tracking-wider">
                  Value (USD)
                </th>
                <th scope="col" className="px-4 py-3 text-left text-md font-bold text-gray-900 tracking-wider">
                  Actual %
                </th>
                <th scope="col" className="px-4 py-3 text-left text-md font-bold text-gray-900 tracking-wider">
                  Your Value
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {positions.map((position, index) => (
                <tr key={index}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="font-medium text-gray-900">{position.name || 'Unknown'}</div>
                      <div className="text-xs text-gray-500 ml-2">
                        ({formatAddress(position.address || '')})
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-gray-900">{formatPercentage(position.allocation || 0)}%</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-gray-900">{formatBalance(position.balance || 0, 6)}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className={`text-gray-900 ${position.priceUsd ? 'font-medium' : 'text-gray-400 italic'}`}>
                      {position.priceUsd ? `$${position.priceUsd.toFixed(2)}` : 'N/A'}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className={`text-gray-900 ${position.valueUsd ? 'font-medium' : 'text-gray-400 italic'}`}>
                      {position.valueUsd ? `$${position.valueUsd.toFixed(2)}` : 'N/A'}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {totalValueUsd > 0 && position.valueUsd ? (
                      <div className="flex items-center">
                        <div className="font-medium">
                          {((position.valueUsd / totalValueUsd) * 100).toFixed(2)}%
                        </div>
                        {/* Show difference from target allocation */}
                        {position.allocation && (
                          <div className={`ml-2 text-xs ${
                            Math.abs((position.valueUsd / totalValueUsd * 100) - (position.allocation / 100)) > 1 
                              ? ((position.valueUsd / totalValueUsd * 100) > (position.allocation / 100) ? 'text-green-600' : 'text-red-600')
                              : 'text-gray-500'
                          }`}>
                            {((position.valueUsd / totalValueUsd * 100) - (position.allocation / 100)).toFixed(2) > 0 
                              ? `+${((position.valueUsd / totalValueUsd * 100) - (position.allocation / 100)).toFixed(2)}%` 
                              : `${((position.valueUsd / totalValueUsd * 100) - (position.allocation / 100)).toFixed(2)}%`}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-gray-400 italic">N/A</div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {position.valueUsd && userOwnershipPercentage > 0 ? (
                      <div className="text-gray-900 font-medium">
                        ${((position.valueUsd * userOwnershipPercentage) / 100).toFixed(2)}
                      </div>
                    ) : (
                      <div className="text-gray-400 italic">N/A</div>
                    )}
                  </td>
                </tr>
              ))}
              
              {/* Add unallocated ETH row if there is any */}
              {unallocatedEth > 0 && (
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="font-medium text-gray-900">Unallocated ETH</div>
                      <div className="text-xs text-gray-500 ml-2">
                        (trading reserves)
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-gray-900">-</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-gray-900">{formatBalance(unallocatedEth, 6)} ETH</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-gray-900 font-medium">
                      ${ethPrice.toFixed(2)}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-gray-900 font-medium">
                      ${unallocatedEthValueUsd.toFixed(2)}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {totalValueUsd > 0 ? (
                      <div className="font-medium">
                        {((unallocatedEthValueUsd / totalValueUsd) * 100).toFixed(2)}%
                      </div>
                    ) : (
                      <div className="text-gray-400 italic">N/A</div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {userOwnershipPercentage > 0 ? (
                      <div className="text-gray-900 font-medium">
                        ${((unallocatedEthValueUsd * userOwnershipPercentage) / 100).toFixed(2)}
                      </div>
                    ) : (
                      <div className="text-gray-400 italic">N/A</div>
                    )}
                  </td>
                </tr>
              )}
              
              {/* Total row */}
              {totalValueUsd > 0 && (
                <tr className="bg-blue-100 font-medium">
                  <td className="px-4 py-3 whitespace-nowrap">
                    Total
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    100%
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    -
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    -
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap font-bold">
                    ${totalValueUsd.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    100%
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap font-bold text-blue-800">
                    ${userValueUsd.toFixed(2)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        // Transaction History View
        <>
          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-gray-600">Loading transactions...</span>
            </div>
          )}
          
          {/* Error message */}
          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-4">
              {error}
            </div>
          )}
          
          {/* Transaction list */}
          {!isLoading && !error && (
            <>
              {transactions.length === 0 ? (
                <div className="text-center py-8 text-gray-500 italic">
                  No transactions found
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transaction
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Block
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Age
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        From
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Method
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Value
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Gas Fee
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentTransactions.map((tx, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <a 
                            href={getExplorerUrl(tx.txHash)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-700"
                          >
                            {formatAddress(tx.txHash)}
                          </a>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {tx.blockNumber}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span title={formatTimestamp(tx.timestamp)}>
                            {getTimeAgo(tx.timestamp)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {formatAddress(tx.from)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            {tx.eventName}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {formatTransactionValue(tx.value)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {calculateGasFee(tx.gasUsed, tx.gasPrice)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              
              {/* Pagination */}
              {transactions.length > transactionsPerPage && (
                <div className="flex justify-between items-center mt-4">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded ${
                      currentPage === 1 
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    Previous
                  </button>
                  
                  <span className="text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 rounded ${
                      currentPage === totalPages 
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
              
              {/* Refresh button */}
              <div className="flex justify-end mt-4">
                <button 
                  className="p-2 rounded-lg text-blue-500 hover:bg-blue-50 focus:outline-none transition flex items-center"
                  onClick={fetchTransactions}
                  disabled={isLoading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                  </svg>
                  Refresh transactions
                </button>
              </div>
            </>
          )}
        </>
      )}
      
      {/* Unallocated ETH info - show only when viewing positions */}
      {!showTransactions && unallocatedEth > 0 && (
        <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 text-sm">
          <p className="font-medium">About Unallocated ETH</p>
          <p className="mt-1">Unallocated ETH represents your funds that are currently held in the fund's trading reserves. These funds can be used for buying tokens or may be temporarily held during rebalancing operations. This ETH is still part of your fund ownership and is included in your total value.</p>
        </div>
      )}
      
      {/* Last updated timestamp - only show on positions view */}
      {!showTransactions && (
        <div className="mt-2 text-xs text-right text-gray-500">
          Token prices refresh automatically every minute
        </div>
      )}
    </>
  );
};

export default PositionsGrid;